import { makeStyles } from '@material-ui/core/styles';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import { Hidden } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { BASE_API_PATH, BLOG_PATH } from '../constants';
type BlogPost = {
    mediaKey: string;
    imageUrl: string;
    title: string;
    timestamp: number;
    id: string;
    description: string;
    category: Exclude<CategoryOption, 'All Posts'>;
};
type BlogPostList = BlogPost[];

type CategoryOption = 'All Posts' | 'Reflections' | 'Life' | 'Projects';

interface Response {
    moreItems: boolean;
    items: BlogPostList;
}

enum MethodType {
    LESS_THAN = 'less_than',
    GREATER_THAN = 'greater_than',
}

const CATEGORY_OPTIONS: CategoryOption[] = ['All Posts', 'Reflections', 'Life', 'Projects'];
const PAGE_SIZE = 5;

const useStyles = makeStyles((theme) => ({
    table: {
        width: '100%',
        margin: 'auto',
    },
    image: {
        width: '138px',
        height: '86px',
        objectFit: 'cover',
        borderRadius: 8,
    },
    tableRow: {
        backgroundColor: '#f8fbff',
        '@media (pointer: fine)': {
            '&:hover': {
                backgroundColor: '#e9f3ff',
                cursor: 'pointer',
            },
        },
    },
}));

function timestampToHumanReadableDate(timestamp: number) {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function getCategoryIcon(category: CategoryOption) {
    const iconStyles = { fontSize: 16, color: 'inherit' };
    switch (category) {
        case 'All Posts':
            return <ArticleOutlinedIcon sx={iconStyles} />;
        case 'Reflections':
            return <LightbulbOutlinedIcon sx={iconStyles} />;
        case 'Life':
            return <FavoriteBorderOutlinedIcon sx={iconStyles} />;
        case 'Projects':
            return <RocketLaunchOutlinedIcon sx={iconStyles} />;
        default:
            return <ArticleOutlinedIcon sx={iconStyles} />;
    }
}

export function Blog() {
    const classes = useStyles();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    const [contentBuffer, setContentBuffer] = useState<BlogPostList>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMoreOlderItems, setHasMoreOlderItems] = useState(false);
    const [error, setError] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<CategoryOption>('All Posts');

    async function getBlogs(timestamp: number, methodType: MethodType): Promise<Response> {
        const response = await fetch(`${BASE_API_PATH}/blog?last_timestamp=${timestamp}&method_type=${methodType}`);
        if (!response.ok) {
            throw new Error('Failed to fetch blog posts');
        }
        return response.json();
    }

    async function handleBackButtonClick() {
        if (currentPage === 0) {
            return;
        }
        setCurrentPage((prevPage) => prevPage - 1);
    }

    async function handleNextButtonClick() {
        const nextPageStartIndex = (currentPage + 1) * PAGE_SIZE;
        if (nextPageStartIndex < contentBuffer.length) {
            setCurrentPage((prevPage) => prevPage + 1);
            return;
        }
        if (!hasMoreOlderItems || contentBuffer.length === 0) {
            return;
        }

        try {
            const oldestTimestamp = contentBuffer[contentBuffer.length - 1].timestamp;
            const content = await getBlogs(oldestTimestamp, MethodType.LESS_THAN);
            if (content.items.length === 0) {
                setHasMoreOlderItems(false);
                return;
            }
            setContentBuffer((prevBuffer) => [...prevBuffer, ...content.items]);
            setHasMoreOlderItems(content.moreItems);
            setCurrentPage((prevPage) => prevPage + 1);
        } catch (error) {
            setError(true);
        }
    }

    useEffect(() => {
        document.title = 'Journal - Mike Brauninger';
        ReactGA.send({ hitType: 'pageview', page: window.location.pathname });
    }, []);

    useEffect(() => {
        const loadInitialBlogs = async () => {
            try {
                const content = await getBlogs(1000000000000, MethodType.LESS_THAN);
                setContentBuffer(content.items);
                setHasMoreOlderItems(content.moreItems);
                setCurrentPage(0);
                setLoading(false);
            } catch (error) {
                setError(true);
                setLoading(false);
            }
        };

        loadInitialBlogs();
    }, []);

    const visibleStartIndex = currentPage * PAGE_SIZE;
    const visibleEndIndex = visibleStartIndex + PAGE_SIZE;
    const content = contentBuffer.slice(visibleStartIndex, visibleEndIndex);
    const backButtonDisabled = currentPage === 0;
    const nextButtonDisabled = !hasMoreOlderItems && visibleEndIndex >= contentBuffer.length;

    const categorizedContent = content;

    const filteredContent =
        selectedCategory === 'All Posts'
            ? categorizedContent
            : categorizedContent.filter((post) => post.category === selectedCategory);

    const categoryCounts = {
        'All Posts': categorizedContent.length,
        Reflections: categorizedContent.filter((post) => post.category === 'Reflections').length,
        Life: categorizedContent.filter((post) => post.category === 'Life').length,
        Projects: categorizedContent.filter((post) => post.category === 'Projects').length,
    };

    return (
        <>
            <Helmet>
                <title>Journal - Mike Brauninger</title>
            </Helmet>
            <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 4, md: 5 } }}>
                <Hidden mdUp>
                    <Typography
                        variant="h3"
                        display={'flex'}
                        justifyContent={'center'}
                        paddingBottom={2}
                        color="#163f86"
                        fontWeight={700}
                    >
                        Journal
                    </Typography>
                </Hidden>
                {loading ? (
                    <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        sx={{
                            maxWidth: 1200,
                            mx: 'auto',
                            borderRadius: 3,
                            border: '1px solid #d9e7f8',
                            backgroundColor: '#f7fbff',
                            py: 6,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box
                        sx={{
                            maxWidth: 1200,
                            mx: 'auto',
                            borderRadius: 3,
                            border: '1px solid #d9e7f8',
                            backgroundColor: '#f7fbff',
                            py: 4,
                        }}
                    >
                        <Typography variant={'h4'} textAlign={'center'} color="#315a93">
                            {`Encountered error when retrieving data.`}
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Hidden mdDown>
                                    <Box
                                        sx={{
                                            width: 250,
                                            flexShrink: 0,
                                            position: 'sticky',
                                            top: 16,
                                        }}
                                    >
                                        <Box sx={{ width: '100%' }}>
                                            <Typography variant="h3" sx={{ color: '#163f86', fontWeight: 700, mb: 1 }}>
                                                Journal
                                            </Typography>
                                            <Typography sx={{ color: '#55729f', fontSize: 14, mb: 4 }}>
                                                Where I share my thoughts and life updates.
                                            </Typography>
                                            <Box
                                                sx={{
                                                    borderRadius: 3,
                                                    border: '1px solid #d9e7f8',
                                                    backgroundColor: '#f7fbff',
                                                    p: 2,
                                                    height: 'fit-content',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: '#55729f',
                                                        fontWeight: 700,
                                                        mb: 1,
                                                        letterSpacing: '0.04em',
                                                    }}
                                                >
                                                    BROWSE
                                                </Typography>
                                                <Stack spacing={0.75}>
                                                    {CATEGORY_OPTIONS.map((category) => {
                                                        const isSelected = selectedCategory === category;
                                                        return (
                                                            <Button
                                                                key={category}
                                                                onClick={() => setSelectedCategory(category)}
                                                                sx={{
                                                                    justifyContent: 'space-between',
                                                                    textTransform: 'none',
                                                                    px: 1.25,
                                                                    py: 0.8,
                                                                    borderRadius: 1.5,
                                                                    color: isSelected ? '#1f4f9c' : '#325887',
                                                                    backgroundColor: isSelected ? '#e6f0ff' : 'transparent',
                                                                    '&:hover': {
                                                                        backgroundColor: '#eaf3ff',
                                                                    },
                                                                }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    {getCategoryIcon(category)}
                                                                    <span>{category}</span>
                                                                </Box>
                                                                <span>{categoryCounts[category]}</span>
                                                            </Button>
                                                        );
                                                    })}
                                                </Stack>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Hidden>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Hidden mdUp>
                                        <Box sx={{ pb: 2 }}>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{ pb: 0.5, justifyContent: 'center', flexWrap: 'wrap', rowGap: 1 }}
                                            >
                                                {CATEGORY_OPTIONS.map((category) => {
                                                    const isSelected = selectedCategory === category;
                                                    return (
                                                        <Chip
                                                            key={category}
                                                            label={category}
                                                            onClick={() => setSelectedCategory(category)}
                                                            sx={{
                                                                backgroundColor: isSelected ? '#2d66c3' : '#eef5ff',
                                                                color: isSelected ? '#ffffff' : '#325887',
                                                                '& .MuiChip-label': {
                                                                    fontWeight: isSelected ? 700 : 500,
                                                                },
                                                                '&.MuiChip-clickable:hover': {
                                                                    backgroundColor: isSelected ? '#2d66c3' : '#eef5ff',
                                                                },
                                                                '&.Mui-focusVisible': {
                                                                    backgroundColor: isSelected ? '#2d66c3' : '#eef5ff',
                                                                },
                                                                '&:active': {
                                                                    backgroundColor: isSelected ? '#2d66c3' : '#eef5ff',
                                                                },
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Stack>
                                        </Box>
                                    </Hidden>

                                    <Box paddingBottom={3}>
                                        {filteredContent.length === 0 ? (
                                            <Box
                                                sx={{
                                                    border: '1px solid #d9e7f8',
                                                    borderRadius: 3,
                                                    backgroundColor: '#f8fbff',
                                                    py: 5,
                                                    px: 2,
                                                }}
                                            >
                                                <Typography textAlign="center" color="#55729f">
                                                    No posts yet.
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Table
                                                className={classes.table}
                                                sx={{
                                                    border: '1px solid #d9e7f8',
                                                    borderRadius: 3,
                                                    overflow: 'hidden',
                                                    backgroundColor: '#f8fbff',
                                                }}
                                            >
                                                <TableBody>
                                                    {filteredContent.map((post) => (
                                                        <TableRow
                                                            key={post.id}
                                                            className={classes.tableRow}
                                                            onClick={() => {
                                                                navigate(`/${BLOG_PATH}/post?id=${post.id}`);
                                                            }}
                                                        >
                                                            <TableCell sx={{ borderColor: '#e3ecf9', py: 1.4 }}>
                                                                <Hidden smUp>
                                                                    <Typography
                                                                        sx={{
                                                                            color: '#4d72a9',
                                                                            fontWeight: 700,
                                                                            fontSize: 12,
                                                                            letterSpacing: '0.04em',
                                                                            textTransform: 'uppercase',
                                                                            pb: 0.4,
                                                                        }}
                                                                    >
                                                                        {post.category}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="h6"
                                                                        style={{
                                                                            fontWeight: 'bold',
                                                                            paddingBottom: 5,
                                                                            color: '#163f86',
                                                                        }}
                                                                    >
                                                                        {post.title}
                                                                    </Typography>
                                                                </Hidden>
                                                                <Box display="flex" alignItems="center">
                                                                    <Box
                                                                        display="flex"
                                                                        alignItems="center"
                                                                        flexDirection={'column'}
                                                                    >
                                                                        <img
                                                                            src={post.imageUrl}
                                                                            alt={post.mediaKey || post.title}
                                                                            className={classes.image}
                                                                        />
                                                                        <Hidden mdUp>
                                                                            <Typography variant="body1" color="#55729f">
                                                                                {timestampToHumanReadableDate(post.timestamp)}
                                                                            </Typography>
                                                                        </Hidden>
                                                                    </Box>
                                                                    <Stack spacing={0.75}>
                                                                        <Hidden smDown>
                                                                            <Typography
                                                                                sx={{
                                                                                    paddingLeft: 2.5,
                                                                                    color: '#4d72a9',
                                                                                    fontWeight: 700,
                                                                                    fontSize: 11.5,
                                                                                    letterSpacing: '0.04em',
                                                                                    textTransform: 'uppercase',
                                                                                }}
                                                                            >
                                                                                {post.category}
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="h6"
                                                                                style={{
                                                                                    paddingLeft: 20,
                                                                                    fontWeight: 'bold',
                                                                                    color: '#163f86',
                                                                                    fontSize: 23,
                                                                                }}
                                                                            >
                                                                                {post.title}
                                                                            </Typography>
                                                                            <Typography style={{ paddingLeft: 20, color: '#325887', fontSize: 14.5 }}>
                                                                                {post.description}
                                                                            </Typography>
                                                                        </Hidden>
                                                                        <Hidden smUp>
                                                                            <Typography
                                                                                style={{
                                                                                    paddingLeft: 20,
                                                                                    fontSize: 13,
                                                                                    position: 'relative',
                                                                                    bottom: 10,
                                                                                    color: '#325887',
                                                                                }}
                                                                            >
                                                                                {post.description}
                                                                            </Typography>
                                                                        </Hidden>
                                                                    </Stack>
                                                                </Box>
                                                            </TableCell>
                                                            <Hidden mdDown>
                                                                <TableCell sx={{ borderColor: '#e3ecf9', py: 1.4 }}>
                                                                    <Typography variant="body2" color="#55729f">
                                                                        {timestampToHumanReadableDate(post.timestamp)}
                                                                    </Typography>
                                                                </TableCell>
                                                            </Hidden>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        <Box
                            maxWidth={1200}
                            mx="auto"
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={'end'}
                            paddingBottom={3}
                            paddingRight={2}
                        >
                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="outlined"
                                    onClick={handleBackButtonClick}
                                    disabled={backButtonDisabled}
                                    startIcon={<ArrowBack />}
                                    sx={{
                                        borderColor: '#9ebdf0',
                                        color: '#1f4f9c',
                                        '&:hover': {
                                            borderColor: '#7da8e8',
                                            backgroundColor: '#eaf3ff',
                                        },
                                    }}
                                >
                                    Back
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleNextButtonClick}
                                    disabled={nextButtonDisabled}
                                    endIcon={<ArrowForward />}
                                    sx={{
                                        backgroundColor: '#2d66c3',
                                        '&:hover': {
                                            backgroundColor: '#2458ad',
                                        },
                                    }}
                                >
                                    Next
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                )}
            </Box>
        </>
    );
}
